import React, { useState } from "react";
import { Send, Mail, MapPin, Phone } from "lucide-react";
import { motion } from "motion/react";
import { animated } from "@react-spring/web";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "../hooks/use-toast";
import { useLocalizedContent } from "../context/LanguageContext";
import { useParallax } from "../hooks/useParallax";
import { useTilt } from "../hooks/useTilt";
import SectionParallax from "./SectionParallax";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { personalInfo, contactSection } = useLocalizedContent();
  const contentShift = useParallax(0.02);
  const emailTilt = useTilt({ max: 10, scale: 1.02 });
  const phoneTilt = useTilt({ max: 10, scale: 1.02 });
  const locationTilt = useTilt({ max: 10, scale: 1.02 });

  const sectionVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 18 },
    show: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 140, damping: 18 },
    },
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock form submission
    setTimeout(() => {
      toast({
        title: contactSection.toast.title,
        description: contactSection.toast.description,
      });
      setFormData({ name: "", email: "", subject: "", message: "" });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <motion.section
      id="contact"
      className="section-fade relative pt-24 pb-[calc(16rem+env(safe-area-inset-bottom))] sm:pb-40 md:pb-48 overflow-hidden"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-900 to-black"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-900/10 via-transparent to-transparent"></div>
      <SectionParallax variant="blue" />

      <div
        className="relative z-10 container mx-auto px-6 lg:px-12 transition-transform duration-500"
        style={{ transform: `translate3d(0, ${contentShift}px, 0)` }}
      >
        <motion.div variants={sectionVariants}>
          {/* Section Header */}
          <motion.div className="text-center mb-16" variants={itemVariants}>
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium tracking-wider uppercase bg-blue-500/10 text-blue-300 border border-blue-500/20 mb-4">
              {contactSection.badge}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {contactSection.heading}
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              {contactSection.description}
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div className="space-y-8" variants={itemVariants}>
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  {contactSection.infoTitle}
                </h3>
                <p className="text-white/60 leading-relaxed">
                  {contactSection.infoDescription}
                </p>
              </div>

              {/* Contact Details */}
              <div className="space-y-4">
                <animated.a
                  href={`mailto:${personalInfo.email}`}
                  ref={emailTilt.ref}
                  style={emailTilt.style}
                  {...emailTilt.bind}
                  className="group flex items-center gap-4 p-4 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-blue-500/50 transition-all duration-300 will-change-transform"
                >
                  <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 group-hover:bg-blue-500/20 transition-colors">
                    <Mail className="w-5 h-5 text-blue-300" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">
                      {contactSection.details.email}
                    </p>
                    <p className="text-white font-medium">
                      {personalInfo.email}
                    </p>
                  </div>
                </animated.a>

                <animated.a
                  href={`tel:${personalInfo.phone}`}
                  ref={phoneTilt.ref}
                  style={phoneTilt.style}
                  {...phoneTilt.bind}
                  className="group flex items-center gap-4 p-4 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-blue-500/50 transition-all duration-300 will-change-transform"
                >
                  <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 group-hover:bg-blue-500/20 transition-colors">
                    <Phone className="w-5 h-5 text-blue-300" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">
                      {contactSection.details.phone}
                    </p>
                    <p className="text-white font-medium">
                      {personalInfo.phone}
                    </p>
                  </div>
                </animated.a>

                <animated.div
                  ref={locationTilt.ref}
                  style={locationTilt.style}
                  {...locationTilt.bind}
                  className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 will-change-transform"
                >
                  <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <MapPin className="w-5 h-5 text-blue-300" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">
                      {contactSection.details.location}
                    </p>
                    <p className="text-white font-medium">
                      {personalInfo.location}
                    </p>
                  </div>
                </animated.div>
              </div>

              {/* Social Links */}
              <motion.div className="pt-6" variants={itemVariants}>
                <p className="text-white/50 text-sm mb-4">
                  {contactSection.details.follow}
                </p>
                <div className="flex gap-3">
                  <motion.a
                    href={`https://${personalInfo.linkedin}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-2 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-blue-500/20 hover:border-blue-500/50 text-white/70 hover:text-white transition-all duration-300 text-sm font-medium"
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {contactSection.details.linkedin}
                  </motion.a>
                  <motion.a
                    href={`https://${personalInfo.github}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-2 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-blue-500/20 hover:border-blue-500/50 text-white/70 hover:text-white transition-all duration-300 text-sm font-medium"
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {contactSection.details.github}
                  </motion.a>
                </div>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              className="p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10"
              variants={itemVariants}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-white/70 text-sm font-medium mb-2"
                  >
                    {contactSection.form.name.label}
                  </label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-blue-500/50 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    placeholder={contactSection.form.name.placeholder}
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-white/70 text-sm font-medium mb-2"
                  >
                    {contactSection.form.email.label}
                  </label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-blue-500/50 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    placeholder={contactSection.form.email.placeholder}
                  />
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block text-white/70 text-sm font-medium mb-2"
                  >
                    {contactSection.form.subject.label}
                  </label>
                  <Input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-blue-500/50 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    placeholder={contactSection.form.subject.placeholder}
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-white/70 text-sm font-medium mb-2"
                  >
                    {contactSection.form.message.label}
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-blue-500/50 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none"
                    placeholder={contactSection.form.message.placeholder}
                  />
                </div>

                <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        {contactSection.form.submitSending}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {contactSection.form.submitIdle}
                      </>
                    )}
                  </Button>
                </motion.div>
              </form>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Contact;
